import { Autocomplete, Box, Button, Chip, InputLabel, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import useDebounce from '../../hooks/useDebounce';
import { jiraChannel } from '../../lib/broadcastChannels';
import { getAllProjectFromJIRA } from '../../modules/jira';
import { saveJiraProjects } from '../../modules/project';

const JiraProjectSelection = ({ linkedProjects = [], handleClose, type, projectId }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [localLinkedProjects, setLocalLinkedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const combinedProjects = [...localLinkedProjects, ...selectedProjects];

  useEffect(() => {
    const mapped = linkedProjects.map(p => ({
      id: p.linked_project_id || p.id,
      key: p.linked_project_key || p.key,
      name: p.linked_project_name || p.name,
    }));
    const isSame =
      mapped.length === localLinkedProjects.length &&
      mapped.every((p, i) =>
        p.id === localLinkedProjects[i]?.id &&
        p.key === localLinkedProjects[i]?.key &&
        p.name === localLinkedProjects[i]?.name
      );
  
    if (!isSame) {
      setLocalLinkedProjects(mapped);
    }
  }, [linkedProjects]);

  const fetchProjects = async (query = '') => {
    setLoading(true);
    try {
      const response = await getAllProjectFromJIRA(false, projectId, query);
      const allProjects = response?.values || [];
      setProjects(allProjects);
      setError(null);
    } catch (err) {
      setError('Failed to fetch projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProjects(debouncedSearchQuery);
  }, [debouncedSearchQuery, localLinkedProjects, selectedProjects]);

  const handleRemoveProject = (projectKey) => {
    setLocalLinkedProjects(prev => prev.filter(p => p.key !== projectKey));
    setSelectedProjects(prev => prev.filter(p => p.key !== projectKey));
    fetchProjects();
  };

  const handleSubmit = async () => {
    const dedupedProjectsMap = new Map();
    combinedProjects.forEach(p => {
      if (!dedupedProjectsMap.has(p.key)) {
        dedupedProjectsMap.set(p.key, {
          linkedProjectId: p.id,
          linkedProjectName: p.name,
          linkedProjectKey: p.key,
        });
      }
    });

    const allSelected = Array.from(dedupedProjectsMap.values());

    if (allSelected.length === 0) return;

    setSubmitting(true);
    try {
      await saveJiraProjects(allSelected, projectId);
      addNotification('success', 'Jira Projects linked successfully!');
      jiraChannel.postMessage({
        type: 'JIRA_CONFIG_CHANGED',
        projectId,
        timestamp: Date.now(),
      });
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to save projects. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <InputLabel className="pb-2">Select Jira Projects</InputLabel>

      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {combinedProjects.map((project) => (
          <Chip
            key={project.key}
            label={`${project.name} (${project.key})`}
            onDelete={() => handleRemoveProject(project.key)}
            color={linkedProjects.some(lp => (lp.linked_project_key || lp.key) === project.key) ? 'success' : 'primary'}
            variant="outlined"
          />
        ))}
      </Box>

      <Autocomplete
        multiple
        id="jira-projects"
        options={projects}
        getOptionLabel={(option) => `${option.name} (${option.key})`}
        value={selectedProjects}
        onChange={(_, newValue) => {
          setSelectedProjects(newValue);
          setError(null);
        }}
        onInputChange={(_, newQuery) => {
          setSearchQuery(newQuery);
        }}
        filterOptions={(options) => options}
        loading={loading}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={loading ? 'Loading projects...' : 'No projects found'}
        getOptionDisabled={(option) =>
          combinedProjects?.some((p) => p.key === option.key)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            error={!!error}
            helperText={error}
            placeholder="Search Jira Projects..."
          />
        )}
      />
      <Button
        variant="contained"
        color={type === 'change' ? 'error' : 'primary'}
        sx={{ mt: 3 }}
        onClick={handleSubmit}
        fullWidth
        disabled={combinedProjects.length === 0 || submitting}
      >
        {submitting ? 'Saving...' : type === 'change' ? 'Modify Projects' : 'Submit'}
      </Button>
    </>
  );
};

export default JiraProjectSelection;
